import { useCallback, useEffect, useRef, useState } from 'react'

import { trackEvent } from 'services/analytics'
import { magChatClient } from 'services/magChatClient'

import { readChatHistory, saveChatHistory } from './history'

const MAX_MESSAGE_LENGTH = 500
const MAX_COMPETITOR_FEEDBACK_LENGTH = 4000
const MAX_ACTIVE_DIALOGUE_MESSAGES = 12
let messageSequence = 0

const createMessageId = prefix => `${prefix}-${Date.now()}-${messageSequence += 1}`

const welcomeMessage = () => ({
  id: createMessageId('welcome'),
  role: 'assistant',
  content: '',
  contentKey: 'mag.welcome',
  createdAt: new Date().toISOString(),
})

const sanitizeDialogueMessage = item => ({
  role: item.role,
  content: String(item.content || '').slice(0, item.role === 'assistant' ? 2000 : 500),
  ...(item.role === 'assistant' && ['ANSWER', 'CLARIFY', 'NOT_FOUND', 'BLOCKED'].includes(item.action)
    ? { action: item.action }
    : {}),
  ...(item.role === 'assistant' && item.action === 'CLARIFY' && Number.isInteger(item.clarificationAttempt)
    ? { clarificationAttempt: Math.min(Math.max(item.clarificationAttempt, 1), 3) }
    : {}),
})

const sanitizeSummaryMessage = item => ({
  id: String(item.id || '').slice(0, 160),
  ...sanitizeDialogueMessage(item),
})

export const buildConversationContext = ({ contextSummary, messages, userMessage }) => {
  const dialogue = [...messages, userMessage].filter(item => !item.contentKey && !item.excludeFromContext)
  const recentDialogue = dialogue.slice(-MAX_ACTIVE_DIALOGUE_MESSAGES)
  const overflow = dialogue.slice(0, Math.max(dialogue.length - MAX_ACTIVE_DIALOGUE_MESSAGES, 0))
  const summarizedIndex = contextSummary?.throughMessageId
    ? overflow.findIndex(item => item.id === contextSummary.throughMessageId)
    : -1

  return {
    conversationSummary: contextSummary?.content || '',
    messages: recentDialogue.slice(0, -1).map(sanitizeDialogueMessage),
    summarizedThroughMessageId: contextSummary?.throughMessageId || '',
    summaryMessages: overflow
      .slice(summarizedIndex >= 0 ? summarizedIndex + 1 : 0)
      .map(sanitizeSummaryMessage),
  }
}

export default function useMagChat({ competitorContext, language, t, userId }) {
  const storedHistory = useRef(readChatHistory(userId))
  const restoredMessages = storedHistory.current?.messages || [welcomeMessage()]
  const conversationId = useRef(storedHistory.current?.conversationId || createMessageId('conversation'))
  const conversationState = useRef(storedHistory.current?.conversationState || '')
  const [contextSummary, setContextSummary] = useState(storedHistory.current?.contextSummary || null)
  const [messages, setMessages] = useState(restoredMessages)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState('')
  const [competitorFeedback, setCompetitorFeedback] = useState(null)
  const pendingFeedback = useRef(null)
  const sending = useRef(false)
  const userMessageCount = useRef(messages.filter(message => message.role === 'user').length)

  useEffect(() => {
    saveChatHistory({
      contextSummary,
      conversationId: conversationId.current,
      conversationState: conversationState.current,
      messages,
      userId,
    })
  }, [contextSummary, messages, userId])

  useEffect(() => {
    setCompetitorFeedback(current => current ? competitorContext : null)
  }, [competitorContext])

  const sendMessage = useCallback(async (message, options = {}) => {
    const trimmedMessage = String(message || '').trim().slice(0, MAX_MESSAGE_LENGTH)
    if (!trimmedMessage || sending.current) return false
    sending.current = true

    const userMessage = {
      id: createMessageId('user'),
      role: 'user',
      content: trimmedMessage,
      createdAt: new Date().toISOString(),
    }

    setError('')
    setMessages(current => [...current, userMessage])
    setIsSending(true)
    userMessageCount.current += 1

    const isCompetitiveHub = Boolean(competitorContext)
    if (isCompetitiveHub && userMessageCount.current === 1) trackEvent('COMPETITIVE_AI_SESSION_STARTED', {
      competitive_section: 'assistant',
      assistant_id: 'maggie',
    })

    trackEvent(isCompetitiveHub ? 'competitive_ai_prompt_sent' : 'mag_message_submitted', {
      page_path: window.location.pathname,
      language,
      competitive_section: isCompetitiveHub ? 'assistant' : undefined,
      assistant_id: isCompetitiveHub ? 'maggie' : undefined,
      source: options.quickActionId ? 'quick_action' : 'composer',
      quick_action_id: options.quickActionId,
      session_message_count: userMessageCount.current,
    })

    try {
      const conversationContext = buildConversationContext({ contextSummary, messages, userMessage })
      const response = await magChatClient.sendMessage({
        ...conversationContext,
        conversationState: conversationState.current,
        message: trimmedMessage,
        route: window.location.pathname,
        locale: language,
        portal: 'universe',
        conversationId: conversationId.current,
      })

      conversationId.current = response.conversationId || conversationId.current
      conversationState.current = response.conversationState || ''
      if (response.conversationSummary && response.summarizedThroughMessageId) {
        setContextSummary({
          content: response.conversationSummary,
          throughMessageId: response.summarizedThroughMessageId,
        })
      }
      if (response.outcome === 'access_denied') setContextSummary(null)
      setMessages(current => [...current, response])
      return true
    } catch (_error) {
      setError(t('mag.local_error'))
      return false
    } finally {
      sending.current = false
      setIsSending(false)
    }
  }, [competitorContext, contextSummary, language, messages, t])

  const startCompetitorFeedback = useCallback((context) => {
    if (!context?.route || !context?.section || competitorFeedback) return false
    setError('')
    setMessages(current => {
      const prompt = {
        id: createMessageId('competitor-feedback-prompt'),
        role: 'assistant',
        content: '',
        contentKey: 'mag.competitor_feedback.prompt',
        createdAt: new Date().toISOString(),
      }
      if (current.length === 1 && current[0]?.contentKey === 'mag.welcome') return [prompt]
      return [...current, prompt]
    })
    return true
  }, [competitorFeedback])

  const openFeedbackForm = useCallback(() => {
    setError('')
    setCompetitorFeedback(competitorContext || { route: window.location.pathname, section: 'maggie' })
  }, [competitorContext])

  const closeFeedbackForm = useCallback(() => {
    if (sending.current) return
    setCompetitorFeedback(null)
    setError('')
  }, [])

  const submitFeedbackForm = useCallback(async feedback => {
    const content = String(feedback || '').trim()
    if (!content || content.length > MAX_COMPETITOR_FEEDBACK_LENGTH || sending.current) return false
    sending.current = true
    setIsSending(true)
    setError('')
    const route = competitorFeedback?.route || window.location.pathname
    const signature = `${route}:${content}`
    if (pendingFeedback.current?.signature !== signature) {
      pendingFeedback.current = { signature, submissionId: createMessageId('feedback') }
    }
    try {
      const input = { feedback: content, route, locale: language, platform: 'web', submissionId: pendingFeedback.current.submissionId }
      if (competitorContext) await magChatClient.submitCompetitorFeedback(input)
      else await magChatClient.sendFeedback(input)
      setMessages(current => [...current, {
        id: createMessageId('feedback-saved'), role: 'assistant', content: '',
        contentKey: 'mag.competitor_feedback.success', excludeFromContext: true,
        createdAt: new Date().toISOString(),
      }])
      setCompetitorFeedback(null)
      pendingFeedback.current = null
      trackEvent('competitor_feedback_submitted', { page_path: route, section: competitorContext?.section || 'maggie' })
      return true
    } catch (_error) {
      setError(t('mag.competitor_feedback.error'))
      return false
    } finally {
      sending.current = false
      setIsSending(false)
    }
  }, [competitorContext, competitorFeedback, language, t])

  const sendFeedback = useCallback(async (message, rating, reason) => {
    if (!message?.requestId || message.feedback) return false
    try {
      await magChatClient.sendFeedback({
        requestId: message.requestId,
        submissionId: `rating-${message.requestId}`,
        route: window.location.pathname,
        platform: 'web',
        outcome: message.outcome,
        sourceCount: message.citations?.length || 0,
        locale: language,
        rating,
        reason,
      })
      setMessages(current => current.map(item => (
        item.id === message.id ? { ...item, feedback: { rating, reason } } : item
      )))
      trackEvent(competitorContext ? 'competitive_ai_feedback_submitted' : 'mag_feedback_submitted', {
        language,
        competitive_section: competitorContext ? 'assistant' : undefined,
        assistant_id: competitorContext ? 'maggie' : undefined,
        outcome: message.outcome,
        source_count: message.citations?.length || 0,
        rating,
        reason,
      })
      return true
    } catch (_error) {
      setError(t('mag.feedback_error'))
      return false
    }
  }, [competitorContext, language, t])

  return {
    error,
    isSending,
    isCompetitorFeedback: Boolean(competitorFeedback),
    maxMessageLength: MAX_MESSAGE_LENGTH,
    openFeedbackForm,
    closeFeedbackForm,
    submitFeedbackForm,
    messages,
    sendFeedback,
    sendMessage,
    startCompetitorFeedback,
  }
}
